/**
 * Guest-facing availability calendar — a month grid marking open vs. booked
 * nights, sourced from `GET /properties/:slug/availability` (12 months of
 * blocked ranges). Read-only when no `onSelect` is given; otherwise a two-click
 * date-range picker that feeds the booking widget.
 *
 * Day math is UTC-based to match how availability_blocks are stored (half-open
 * `[starts_at, ends_at)` at UTC midnight, where ends_at is the check-out morning
 * — so the check-out day is NOT a blocked night). See routes/bookings.ts.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  type BlockedRange,
  ymd,
  addDays,
  todayYmd,
  blockedNightSet,
} from '@/lib/availability';

export type { BlockedRange };

interface AvailabilityCalendarProps {
  /** Fetches availability when `blocks` is not supplied. */
  slug?: string;
  /** Pre-loaded blocked ranges (overrides fetch; used by tests/callers). */
  blocks?: BlockedRange[];
  /** Parent-owned fetch state: true while the parent is still loading the
   * blocks it passes via `blocks` — dates are unselectable meanwhile. */
  blocksLoading?: boolean;
  /** Parent-owned fetch state: true when the parent's availability fetch
   * failed — a subtle warning renders so "everything open" isn't trusted. */
  blocksFailed?: boolean;
  monthsToShow?: number;
  /** Controlled selection (YYYY-MM-DD). */
  checkIn?: string | null;
  checkOut?: string | null;
  /** When provided, days are clickable and a chosen range is emitted here. */
  onSelect?: (range: { checkIn: string | null; checkOut: string | null }) => void;
  /** First month to show (YYYY-MM-DD); defaults to the current month. */
  initialViewDate?: string;
  className?: string;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function monthCells(year: number, month: number): (string | null)[] {
  const firstWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(ymd(year, month, d));
  return cells;
}

export default function AvailabilityCalendar({
  slug,
  blocks: blocksProp,
  blocksLoading = false,
  blocksFailed = false,
  monthsToShow = 1,
  checkIn = null,
  checkOut = null,
  onSelect,
  initialViewDate,
  className,
}: AvailabilityCalendarProps) {
  const { t, i18n } = useTranslation('properties');

  const [fetched, setFetched] = useState<BlockedRange[] | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  useEffect(() => {
    if (blocksProp || !slug) return;
    let alive = true;
    setFetchFailed(false);
    api
      .get<{ blocks: BlockedRange[] }>(`/properties/${slug}/availability`)
      .then((r) => { if (alive) setFetched(r.blocks || []); })
      // Distinguish "no blocks" from "failed to load": a failure keeps
      // `fetched` null and raises the warning below instead of silently
      // rendering every date as available (issue #485).
      .catch(() => { if (alive) { setFetched(null); setFetchFailed(true); } });
    return () => { alive = false; };
  }, [slug, blocksProp]);
  const blocks = blocksProp ?? fetched ?? [];

  // While blocks are in-flight (own fetch or parent's), date selection is
  // suspended — otherwise a quick click lands on a not-yet-marked booked night.
  const loading =
    blocksLoading || (!blocksProp && !!slug && fetched === null && !fetchFailed);
  const loadFailed = blocksFailed || fetchFailed;

  const blockedNights = useMemo(() => blockedNightSet(blocks), [blocks]);

  const today = todayYmd();
  const nowY = new Date().getFullYear();
  const nowM = new Date().getMonth();
  const [view, setView] = useState(() => {
    if (initialViewDate) {
      const [y, m] = initialViewDate.split('-').map(Number);
      return { y, m: m - 1 };
    }
    return { y: nowY, m: nowM };
  });

  const canGoPrev = view.y > nowY || (view.y === nowY && view.m > nowM);
  function shift(delta: number) {
    setView((v) => {
      const d = new Date(Date.UTC(v.y, v.m + delta, 1));
      return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
    });
  }

  function rangeHasBlockedNight(from: string, to: string): boolean {
    for (let d = from; d < to; d = addDays(d, 1)) if (blockedNights.has(d)) return true;
    return false;
  }

  function handleClick(day: string) {
    if (!onSelect || loading || day < today || blockedNights.has(day)) return;
    // Fresh pick (or restarting after a complete range) → set check-in.
    if (!checkIn || (checkIn && checkOut)) {
      onSelect({ checkIn: day, checkOut: null });
      return;
    }
    // Second click before/at check-in, or across a booked night → restart.
    if (day <= checkIn || rangeHasBlockedNight(checkIn, day)) {
      onSelect({ checkIn: day, checkOut: null });
      return;
    }
    onSelect({ checkIn, checkOut: day });
  }

  // Format in UTC so labels line up with the UTC day math above (a local-tz
  // formatter would shift Date.UTC(...) by the viewer's offset, mislabeling days).
  const monthFmt = new Intl.DateTimeFormat(i18n?.language || 'en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const dateFmt = new Intl.DateTimeFormat(i18n?.language || 'en', { dateStyle: 'long', timeZone: 'UTC' });

  const months: { y: number; m: number }[] = [];
  for (let i = 0; i < Math.max(1, monthsToShow); i++) {
    const d = new Date(Date.UTC(view.y, view.m + i, 1));
    months.push({ y: d.getUTCFullYear(), m: d.getUTCMonth() });
  }

  function renderMonth(year: number, month: number, isFirst: boolean, isLast: boolean) {
    const cells = monthCells(year, month);
    const label = monthFmt.format(new Date(Date.UTC(year, month, 1)));
    return (
      <div key={`${year}-${month}`} className="flex-1 min-w-[240px]">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => shift(-1)}
            disabled={!canGoPrev}
            aria-label={t('availabilityCalendar.prevMonth', { defaultValue: 'Previous month' })}
            className={cn('p-1 rounded hover:bg-surface-muted disabled:opacity-30', !isFirst && 'invisible')}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-ink">{label}</span>
          <button
            type="button"
            onClick={() => shift(1)}
            aria-label={t('availabilityCalendar.nextMonth', { defaultValue: 'Next month' })}
            className={cn('p-1 rounded hover:bg-surface-muted', !isLast && 'invisible')}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 mb-0.5" aria-hidden="true">
          {WEEKDAYS.map((w, i) => (
            <div key={i} className="text-xs text-center text-ink-muted py-1">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((day, i) => {
            if (!day) return <div key={i} aria-hidden="true" />;
            const dayNum = Number(day.slice(8, 10));
            const isPast = day < today;
            const isBlocked = blockedNights.has(day);
            const disabled = isPast || isBlocked;
            const isCheckIn = day === checkIn;
            const isCheckOut = day === checkOut;
            const inRange = !!checkIn && !!checkOut && day > checkIn && day < checkOut;
            const selectable = !!onSelect && !disabled && !loading;
            const dateLabel = dateFmt.format(new Date(Date.UTC(year, month, dayNum)));
            return (
              <button
                key={i}
                type="button"
                disabled={!selectable}
                onClick={() => handleClick(day)}
                aria-pressed={isCheckIn || isCheckOut}
                aria-label={`${dateLabel} — ${
                  disabled
                    ? t('availabilityCalendar.unavailable', { defaultValue: 'unavailable' })
                    : t('availabilityCalendar.available', { defaultValue: 'available' })
                }`}
                className={cn(
                  'aspect-square text-xs rounded flex items-center justify-center',
                  (isCheckIn || isCheckOut) && 'bg-cta text-white font-semibold',
                  inRange && 'bg-sage-100 text-sage-900',
                  !isCheckIn && !isCheckOut && !inRange && !disabled && 'text-ink hover:bg-sage-50',
                  disabled && 'text-ink-muted line-through cursor-not-allowed',
                  !selectable && !disabled && 'cursor-default',
                )}
              >
                {dayNum}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex gap-6 flex-col sm:flex-row">
        {months.map((mm, idx) => renderMonth(mm.y, mm.m, idx === 0, idx === months.length - 1))}
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-ink-muted">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-line-strong bg-surface inline-block" />
          {t('availabilityCalendar.legendAvailable', { defaultValue: 'Available' })}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-surface-muted inline-block" />
          {t('availabilityCalendar.legendBooked', { defaultValue: 'Booked' })}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-cta inline-block" />
          {t('availabilityCalendar.legendSelected', { defaultValue: 'Selected' })}
        </span>
      </div>
      {loadFailed && (
        <p role="status" className="mt-2 text-xs text-amber-600">
          {t('availabilityCalendar.loadFailed', {
            defaultValue: "We couldn't load booked dates — some dates shown as available may already be taken.",
          })}
        </p>
      )}
    </div>
  );
}
